import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Button, Text, Group, Badge } from '@repletoryreach/sdk-ui';
import { TextInput, Textarea, Select, Switch, NumberInput, Checkbox, Radio, RadioGroup, Fieldset } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { BotChannelsCard } from './BotChannelsCard';
import { BotActions } from './BotActions';
import { useServices } from '../../../bookings/lib/data';
import { useI18n } from '../../../../i18n/I18nProvider';
import { WordsInput } from '../../../../components/WordsInput';
import { byRegion, languageLabel, languageOptions, useLanguages } from '../../../../lib/languages';
import '../assistant.css';

/** The behaviour settings as the form edits them: word lists as one word per line. */
interface BehaviourForm {
  smallTalkEnabled: boolean;
  thanksReply: string;
  acknowledgeReply: string;
  goodbyeReply: string;
  moderationEnabled: boolean;
  moderationAction: flows.ModerationAction;
  moderationReply: string;
  extraWords: string;
  allowedWords: string;
  noticeEnabled: boolean;
  noticeMessage: string;
  /** Take-over and hand-back lines; blank means the default shown as the placeholder (4 October 2026). */
  joinedMessage: string;
  backMessage: string;
  defaultJoined: string;
  defaultBack: string;
  /** Bookings and requests the bot may handle for a customer; both off until switched on (16 October 2026). */
  bookings: boolean;
  tickets: boolean;
  voiceNotes: boolean;
  photos: boolean;
  documents: boolean;
  /** When a customer goes quiet on a question (18 October 2026): check in, then close. Blank lines are the defaults. */
  idleEnabled: boolean;
  nudgeAfter: number | '';
  closeAfter: number | '';
  nudgeMessage: string;
  closeMessage: string;
  defaultNudge: string;
  defaultClose: string;
  /** When a message matches more than one flow (18 October 2026): the assistant chooses, or the customer is always asked. */
  choiceMode: 'assistant' | 'ask';
  choiceQuestion: string;
  defaultChoiceQuestion: string;
  /** The languages it speaks besides the main one, and how a customer gets theirs (1 November 2026). */
  offered: string[];
  languageMode: flows.BotLanguageMode;
  changeWords: string[];
}

/** The minutes either wait may be, as the server allows. */
const IDLE_MIN = 1;
const IDLE_MAX = 720;

/** Languages besides the main one, and the business's own change words, as the server allows. */
const MAX_OFFERED = 12;
const MAX_CHANGE_WORDS = 20;
const LANGUAGE_MODES: readonly flows.BotLanguageMode[] = ['ask', 'follow', 'contact'];

const lines = (text: string) => text.split('\n').map((w) => w.trim()).filter((w) => w.length > 0);

/**
 * The main language as a code from the language list. The server resolves an old free-text value ("English",
 * "Afrikaans") into `behaviour.languages.main`, so that is what the select shows.
 */
const mainOf = (bot: flows.Bot): string => bot.behaviour.languages?.main || bot.language?.trim() || 'en';

const personaOf = (bot: flows.Bot) => ({
  name: bot.name ?? '',
  business_description: bot.business_description ?? '',
  tone: bot.tone ?? 'friendly',
  language: mainOf(bot),
  greeting: bot.greeting ?? ''
});

const behaviourOf = (bot: flows.Bot): BehaviourForm => {
  const b = bot.behaviour;
  return {
    smallTalkEnabled: b.small_talk.enabled,
    thanksReply: b.small_talk.thanks_reply,
    acknowledgeReply: b.small_talk.acknowledge_reply,
    goodbyeReply: b.small_talk.goodbye_reply,
    moderationEnabled: b.moderation.enabled,
    moderationAction: (b.moderation.action as flows.ModerationAction) ?? 'warn',
    moderationReply: b.moderation.reply,
    extraWords: b.moderation.extra_words.join('\n'),
    allowedWords: b.moderation.allowed_words.join('\n'),
    noticeEnabled: b.pause_notice.enabled,
    noticeMessage: b.pause_notice.message,
    joinedMessage: b.handover?.joined_message ?? '',
    backMessage: b.handover?.back_message ?? '',
    defaultJoined: b.handover?.default_joined_message ?? '',
    defaultBack: b.handover?.default_back_message ?? '',
    bookings: b.abilities?.bookings ?? false,
    tickets: b.abilities?.tickets ?? false,
    voiceNotes: b.media?.voice_notes ?? true,
    photos: b.media?.photos ?? true,
    documents: b.media?.documents ?? true,
    idleEnabled: b.idle?.enabled ?? true,
    nudgeAfter: b.idle?.nudge_after_minutes ?? 10,
    closeAfter: b.idle?.close_after_minutes ?? 10,
    nudgeMessage: b.idle?.nudge_message ?? '',
    closeMessage: b.idle?.close_message ?? '',
    defaultNudge: b.idle?.default_nudge_message ?? '',
    defaultClose: b.idle?.default_close_message ?? '',
    choiceMode: b.flow_choice?.mode === 'ask' ? 'ask' : 'assistant',
    choiceQuestion: b.flow_choice?.question ?? '',
    defaultChoiceQuestion: b.flow_choice?.default_question ?? '',
    offered: b.languages?.offered ?? [],
    languageMode: LANGUAGE_MODES.find((m) => m === b.languages?.mode) ?? 'ask',
    changeWords: b.languages?.change_words ?? []
  };
};

/**
 * One chatbot: who it is (name, what the business does, tone, language,
 * greeting) and, since 15 September 2026, how it behaves beyond answering.
 * Small talk gets the organisation's own short replies; language it does not
 * accept is answered and flagged; and a customer who writes while a person has
 * the conversation is told so. Every AI answer step of this chatbot's flows
 * speaks as this, and {{bot.name}} works in any message they send.
 *
 * `multi` is whether the organisation has more than one chatbot (21 September
 * 2026). With one, this is the page it has always been; with several, it also
 * says where this chatbot answers and lets it be made the default or deleted.
 * Mount it with `key={bot.id}`: the form starts from the bot it is given.
 */
export function BotEditor({ bot, multi }: { bot: flows.Bot; multi: boolean }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState(() => personaOf(bot));
  // The bot as last saved: an untouched main language goes back as it was stored, so opening and saving the page
  // never rewrites an old "English" by itself.
  const [saved, setSaved] = useState(bot);
  const languagesQ = useLanguages();
  const [behaviour, setBehaviour] = useState<BehaviourForm | null>(() => behaviourOf(bot));
  const defaultWordCount = bot.behaviour.moderation.default_word_count ?? 0;
  const [showDefaults, setShowDefaults] = useState(false);
  // Only to say when booking is on but there is nothing to book yet.
  const services = useServices();
  const defaults = useQuery({
    queryKey: ['bot-profile', 'not-accepted-words'],
    queryFn: () => flows.getNotAcceptedWords(),
    enabled: showDefaults,
    staleTime: 3_600_000
  });

  const save = useMutation({
    mutationFn: () => flows.updateBot(bot.id, {
      name: form.name.trim(),
      business_description: form.business_description.trim() || null,
      tone: form.tone,
      language: form.language === mainOf(saved) ? saved.language ?? null : form.language,
      greeting: form.greeting.trim() || null,
      behaviour: behaviour ? {
        small_talk: {
          enabled: behaviour.smallTalkEnabled,
          thanks_reply: behaviour.thanksReply.trim(),
          acknowledge_reply: behaviour.acknowledgeReply.trim(),
          goodbye_reply: behaviour.goodbyeReply.trim()
        },
        moderation: {
          enabled: behaviour.moderationEnabled,
          action: behaviour.moderationAction,
          reply: behaviour.moderationReply.trim(),
          extra_words: lines(behaviour.extraWords),
          allowed_words: lines(behaviour.allowedWords)
        },
        pause_notice: {
          enabled: behaviour.noticeEnabled,
          message: behaviour.noticeMessage.trim()
        },
        abilities: { bookings: behaviour.bookings, tickets: behaviour.tickets },
        media: { voice_notes: behaviour.voiceNotes, photos: behaviour.photos, documents: behaviour.documents },
        handover: {
          joined_message: behaviour.joinedMessage.trim() || null,
          back_message: behaviour.backMessage.trim() || null
        },
        idle: {
          enabled: behaviour.idleEnabled,
          nudge_after_minutes: behaviour.nudgeAfter === '' ? undefined : behaviour.nudgeAfter,
          close_after_minutes: behaviour.closeAfter === '' ? undefined : behaviour.closeAfter,
          nudge_message: behaviour.nudgeMessage.trim() || null,
          close_message: behaviour.closeMessage.trim() || null
        },
        flow_choice: { mode: behaviour.choiceMode, question: behaviour.choiceQuestion.trim() || null },
        languages: {
          offered: offeredNow(behaviour.offered),
          mode: behaviour.languageMode,
          change_words: behaviour.changeWords
        }
      } : null
    }),
    onSuccess: (res) => {
      // The server cleans the word lists and fills blank replies with the
      // defaults; show what was saved rather than what was typed.
      setSaved(res.data);
      setForm(personaOf(res.data));
      setBehaviour(behaviourOf(res.data));
      void qc.invalidateQueries({ queryKey: ['bots'] });
      void qc.invalidateQueries({ queryKey: ['bot-profile'] });
      notifySuccess(t('settings.assistant.saved', { name: form.name.trim() }), t('settings.assistant.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.assistant.title'))
  });

  /**
   * The other languages as they can be saved: never the main one, and only languages the list still has. One the
   * platform has switched off since is dropped, because the server refuses a chatbot that offers it.
   */
  function offeredNow(offered: string[]): string[] {
    const listed = languagesQ.languages;
    return offered.filter((code) => code !== form.language && (listed.length === 0 || listed.some((l) => l.code === code)));
  }

  const minutesOk = (m: number | '') => m === '' || (m >= IDLE_MIN && m <= IDLE_MAX);
  const canSave = form.name.trim().length > 0 && form.name.trim().length <= 40
    && (!behaviour || (minutesOk(behaviour.nudgeAfter) && minutesOk(behaviour.closeAfter)));
  const example = form.name.trim() || t('settings.assistant.defaultName');
  const edit = (patch: Partial<BehaviourForm>) => setBehaviour((b) => (b ? { ...b, ...patch } : b));

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <div>
            <Title order={4}>{t('settings.assistant.title')}</Title>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.intro')}</Text>
          </div>
          <TextInput
            label={t('settings.assistant.name')}
            description={t('settings.assistant.nameHint')}
            value={form.name}
            maxLength={40}
            placeholder={'Sarah' /* i18n-ignore: an example name */}
            onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
          />
          <Textarea
            label={t('settings.assistant.business')}
            description={t('settings.assistant.businessHint')}
            value={form.business_description}
            minRows={2}
            maxLength={500}
            placeholder={t('settings.assistant.businessPlaceholder')}
            onChange={(e) => setForm({ ...form, business_description: e.currentTarget.value })}
          />
          <Select
            label={t('settings.assistant.tone')}
            description={t('settings.assistant.toneHint')}
            value={form.tone}
            onChange={(e) => setForm({ ...form, tone: e.currentTarget.value })}
            data={(['friendly', 'formal', 'playful'] as const).map((v) => ({ value: v, label: t(`settings.assistant.tones.${v}`) }))}
          />
          <Textarea
            label={t('settings.assistant.greeting')}
            description={t('settings.assistant.greetingHint')}
            value={form.greeting}
            minRows={2}
            maxLength={500}
            placeholder={t('settings.assistant.greetingPlaceholder', { name: example })}
            onChange={(e) => setForm({ ...form, greeting: e.currentTarget.value })}
          />
          <Card withBorder className="rr-settings__preview">
            <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.assistant.previewLabel')}</Text>
            <Text role="bodySmall">{t('settings.assistant.preview', { name: example })}</Text>
          </Card>
        </Stack>
      </Card>

      {behaviour && (
        <>
          {/* Languages (1 November 2026): one knowledge base, answered in each customer's language. The main language
              is the one the knowledge base and flows are written in; a question in another is put into it first. */}
          <Card withBorder data-testid="assistant-languages" data-guide="assistant-languages">
            <Stack gap="md">
              <div>
                <Title order={5}>{t('settings.assistant.languages.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.languages.intro')}</Text>
              </div>
              {languagesQ.isError && (
                <Alert color="error" data-testid="languages-load-error">
                  <Group justify="space-between" gap="sm" wrap="wrap">
                    <Text role="bodySmall">{t('settings.assistant.languages.loadError')}</Text>
                    <Button size="xs" variant="outline" onClick={() => void languagesQ.refetch()}>{t('action.retry')}</Button>
                  </Group>
                </Alert>
              )}
              <Select
                label={t('settings.assistant.languages.main')}
                description={t('settings.assistant.languages.mainHint')}
                value={form.language}
                disabled={languagesQ.isPending}
                onChange={(e) => {
                  const main = e.currentTarget.value;
                  setForm({ ...form, language: main });
                  // The main language is always spoken, so it is not also one of the others.
                  edit({ offered: behaviour.offered.filter((code) => code !== main) });
                }}
                data={languageOptions(languagesQ.languages, locale, form.language)}
                style={{ maxWidth: 360 }}
                data-testid="languages-main"
              />
              {languagesQ.languages.length > 0 && (
                <Fieldset variant="unstyled" legend={t('settings.assistant.languages.offered')} data-testid="languages-offered">
                  {byRegion(languagesQ.languages).map(({ region, languages }) => (
                    <Stack key={region} gap="xs">
                      <Text role="overline" c="var(--rr-color-text-muted)">{t(`settings.assistant.languages.region.${region}`, region)}</Text>
                      <div className="rr-assistant-languages__grid">
                        {languages.map((l) => {
                          const isMain = l.code === form.language;
                          const checked = isMain || behaviour.offered.includes(l.code);
                          const full = !checked && offeredNow(behaviour.offered).length >= MAX_OFFERED;
                          return (
                            <Checkbox
                              key={l.code}
                              checked={checked}
                              disabled={isMain || full}
                              onChange={(on) => edit({
                                offered: on ? [...behaviour.offered, l.code] : behaviour.offered.filter((code) => code !== l.code)
                              })}
                              label={
                                <span className="rr-assistant-languages__label">
                                  <span>{languageLabel(l, locale)}</span>
                                  {l.quality === 'review' && (
                                    <Badge size="xs" variant="light" color="orange">{t('settings.assistant.languages.reviewBadge')}</Badge>
                                  )}
                                </span>
                              }
                              description={isMain ? t('settings.assistant.languages.isMain') : undefined}
                              data-testid={`language-offer-${l.code}`}
                            />
                          );
                        })}
                      </div>
                    </Stack>
                  ))}
                  <Stack gap={4}>
                    <Text role="caption" c="var(--rr-color-text-muted)" data-testid="languages-offered-count">
                      {offeredNow(behaviour.offered).length === 0
                        ? t('settings.assistant.languages.offeredNone', { max: MAX_OFFERED })
                        : t('settings.assistant.languages.offeredCount', { count: offeredNow(behaviour.offered).length, max: MAX_OFFERED })}
                    </Text>
                    {languagesQ.languages.some((l) => l.quality === 'review') && (
                      <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.assistant.languages.reviewNote')}</Text>
                    )}
                  </Stack>
                </Fieldset>
              )}
              <RadioGroup
                label={t('settings.assistant.languages.mode')}
                value={behaviour.languageMode}
                onChange={(v) => edit({ languageMode: LANGUAGE_MODES.find((m) => m === v) ?? 'ask' })}
              >
                {LANGUAGE_MODES.map((m) => (
                  <Radio
                    key={m}
                    value={m}
                    disabled={offeredNow(behaviour.offered).length === 0}
                    label={t(`settings.assistant.languages.modes.${m}`)}
                    description={t(`settings.assistant.languages.modeHint.${m}`)}
                    data-testid={`language-mode-${m}`}
                  />
                ))}
              </RadioGroup>
              <WordsInput
                label={t('settings.assistant.languages.changeWords')}
                description={t('settings.assistant.languages.changeWordsHint', { max: MAX_CHANGE_WORDS })}
                value={behaviour.changeWords}
                onChange={(words) => edit({ changeWords: words })}
                max={MAX_CHANGE_WORDS}
                maxLength={40}
                lowercase
                disabled={offeredNow(behaviour.offered).length === 0}
                data-testid="language-change-words"
              />
              <Text role="caption" c="var(--rr-color-text-muted)" data-testid="languages-charge">{t('settings.assistant.languages.charge')}</Text>
            </Stack>
          </Card>

          {/* Bookings and requests (16 October 2026): what the bot may do for a customer besides answering. */}
          <Card withBorder data-testid="assistant-abilities">
            <Stack gap="md">
              <div>
                <Title order={5}>{t('settings.assistant.abilities.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.abilities.intro')}</Text>
              </div>
              <Switch
                label={t('settings.assistant.abilities.bookings')}
                description={t('settings.assistant.abilities.bookingsHint')}
                checked={behaviour.bookings}
                onChange={(checked) => edit({ bookings: checked })}
                data-testid="ability-bookings"
              />
              {behaviour.bookings && services.data && services.data.data.length === 0 && (
                <Alert color="warning" data-testid="ability-no-services">
                  {t('settings.assistant.abilities.noServices')} <Link to="/bookings/services">{t('settings.assistant.abilities.addServices')}</Link>
                </Alert>
              )}
              <Switch
                label={t('settings.assistant.abilities.tickets')}
                description={t('settings.assistant.abilities.ticketsHint')}
                checked={behaviour.tickets}
                onChange={(checked) => edit({ tickets: checked })}
                data-testid="ability-tickets"
              />
            </Stack>
          </Card>

          {/* Voice notes, photos and documents (27 October 2026): read into words so the chatbot can answer them. */}
          <Card withBorder data-testid="assistant-media">
            <Stack gap="md">
              <div>
                <Title order={5}>{t('settings.assistant.media.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.media.intro')}</Text>
              </div>
              <Switch
                label={t('settings.assistant.media.voiceNotes')}
                description={t('settings.assistant.media.voiceNotesHint')}
                checked={behaviour.voiceNotes}
                onChange={(checked) => edit({ voiceNotes: checked })}
                data-testid="media-voice-notes"
              />
              <Switch
                label={t('settings.assistant.media.photos')}
                description={t('settings.assistant.media.photosHint')}
                checked={behaviour.photos}
                onChange={(checked) => edit({ photos: checked })}
                data-testid="media-photos"
              />
              <Switch
                label={t('settings.assistant.media.documents')}
                description={t('settings.assistant.media.documentsHint')}
                checked={behaviour.documents}
                onChange={(checked) => edit({ documents: checked })}
                data-testid="media-documents"
              />
            </Stack>
          </Card>

          {/* When a customer goes quiet on a question (18 October 2026): "Are you still there?", then the chat closes,
              so the next message starts afresh instead of being taken as an answer to an old question. */}
          <Card withBorder data-testid="assistant-idle">
            <Stack gap="md">
              <div>
                <Title order={5}>{t('settings.assistant.idle.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.idle.intro')}</Text>
              </div>
              <Switch
                label={t('settings.assistant.idle.enabled')}
                checked={behaviour.idleEnabled}
                onChange={(checked) => edit({ idleEnabled: checked })}
                data-testid="idle-enabled"
              />
              <Group gap="md" align="flex-start" wrap="wrap">
                <NumberInput
                  label={t('settings.assistant.idle.nudgeAfter')}
                  description={t('settings.assistant.idle.minutesHint')}
                  value={behaviour.nudgeAfter}
                  min={IDLE_MIN}
                  max={IDLE_MAX}
                  disabled={!behaviour.idleEnabled}
                  error={minutesOk(behaviour.nudgeAfter) ? undefined : t('settings.assistant.idle.minutesError', { min: IDLE_MIN, max: IDLE_MAX })}
                  onChange={(v) => edit({ nudgeAfter: v })}
                  style={{ maxWidth: 240 }}
                  data-testid="idle-nudge-after"
                />
                <NumberInput
                  label={t('settings.assistant.idle.closeAfter')}
                  description={t('settings.assistant.idle.closeAfterHint')}
                  value={behaviour.closeAfter}
                  min={IDLE_MIN}
                  max={IDLE_MAX}
                  disabled={!behaviour.idleEnabled}
                  error={minutesOk(behaviour.closeAfter) ? undefined : t('settings.assistant.idle.minutesError', { min: IDLE_MIN, max: IDLE_MAX })}
                  onChange={(v) => edit({ closeAfter: v })}
                  style={{ maxWidth: 240 }}
                  data-testid="idle-close-after"
                />
              </Group>
              <Textarea
                label={t('settings.assistant.idle.nudgeMessage')}
                description={t('settings.assistant.idle.nudgeHint')}
                placeholder={behaviour.defaultNudge}
                value={behaviour.nudgeMessage}
                minRows={2}
                maxLength={500}
                disabled={!behaviour.idleEnabled}
                onChange={(e) => edit({ nudgeMessage: e.currentTarget.value })}
                data-testid="idle-nudge-message"
              />
              <Textarea
                label={t('settings.assistant.idle.closeMessage')}
                description={t('settings.assistant.idle.closeHint')}
                placeholder={behaviour.defaultClose}
                value={behaviour.closeMessage}
                minRows={2}
                maxLength={500}
                disabled={!behaviour.idleEnabled}
                onChange={(e) => edit({ closeMessage: e.currentTarget.value })}
                data-testid="idle-close-message"
              />
              <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.assistant.idle.always')}</Text>
            </Stack>
          </Card>

          {/* When a message matches more than one flow (18 October 2026): two flows with "book a tour", or a keyword
              misspelt. The assistant chooses using the conversation and asks when unsure, or the customer is always asked. */}
          <Card withBorder data-testid="assistant-flow-choice">
            <Stack gap="md">
              <div>
                <Title order={5}>{t('settings.assistant.flowChoice.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.flowChoice.intro')}</Text>
              </div>
              <Select
                label={t('settings.assistant.flowChoice.mode')}
                description={t(`settings.assistant.flowChoice.modeHint.${behaviour.choiceMode}`)}
                value={behaviour.choiceMode}
                onChange={(e) => edit({ choiceMode: e.currentTarget.value === 'ask' ? 'ask' : 'assistant' })}
                data={[
                  { value: 'assistant', label: t('settings.assistant.flowChoice.modes.assistant') },
                  { value: 'ask', label: t('settings.assistant.flowChoice.modes.ask') }
                ]}
                data-testid="flow-choice-mode"
              />
              <Textarea
                label={t('settings.assistant.flowChoice.question')}
                description={t('settings.assistant.flowChoice.questionHint')}
                placeholder={behaviour.defaultChoiceQuestion}
                value={behaviour.choiceQuestion}
                minRows={2}
                maxLength={500}
                onChange={(e) => edit({ choiceQuestion: e.currentTarget.value })}
                data-testid="flow-choice-question"
              />
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="md">
              <div>
                <Title order={5}>{t('settings.assistant.smallTalk.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.smallTalk.intro')}</Text>
              </div>
              <Switch
                label={t('settings.assistant.smallTalk.enabled')}
                checked={behaviour.smallTalkEnabled}
                onChange={(checked) => edit({ smallTalkEnabled: checked })}
              />
              <Textarea
                label={t('settings.assistant.smallTalk.thanks')}
                description={t('settings.assistant.smallTalk.hint')}
                value={behaviour.thanksReply}
                minRows={1}
                maxLength={500}
                disabled={!behaviour.smallTalkEnabled}
                onChange={(e) => edit({ thanksReply: e.currentTarget.value })}
              />
              <Textarea
                label={t('settings.assistant.smallTalk.acknowledge')}
                description={t('settings.assistant.smallTalk.hint')}
                value={behaviour.acknowledgeReply}
                minRows={1}
                maxLength={500}
                disabled={!behaviour.smallTalkEnabled}
                onChange={(e) => edit({ acknowledgeReply: e.currentTarget.value })}
              />
              <Textarea
                label={t('settings.assistant.smallTalk.goodbye')}
                description={t('settings.assistant.smallTalk.hint')}
                value={behaviour.goodbyeReply}
                minRows={1}
                maxLength={500}
                disabled={!behaviour.smallTalkEnabled}
                onChange={(e) => edit({ goodbyeReply: e.currentTarget.value })}
              />
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="md">
              <div>
                <Title order={5}>{t('settings.assistant.moderation.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.moderation.intro')}</Text>
              </div>
              <Switch
                label={t('settings.assistant.moderation.enabled')}
                checked={behaviour.moderationEnabled}
                onChange={(checked) => edit({ moderationEnabled: checked })}
              />
              <Textarea
                label={t('settings.assistant.moderation.reply')}
                description={t('settings.assistant.moderation.replyHint')}
                value={behaviour.moderationReply}
                minRows={2}
                maxLength={500}
                disabled={!behaviour.moderationEnabled}
                onChange={(e) => edit({ moderationReply: e.currentTarget.value })}
              />
              <Select
                label={t('settings.assistant.moderation.action')}
                description={t('settings.assistant.moderation.actionHint')}
                value={behaviour.moderationAction}
                disabled={!behaviour.moderationEnabled}
                onChange={(e) => edit({ moderationAction: e.currentTarget.value as flows.ModerationAction })}
                data={(['warn', 'handover'] as const).map((v) => ({ value: v, label: t(`settings.assistant.moderation.actions.${v}`) }))}
              />
              <Textarea
                label={t('settings.assistant.moderation.extra')}
                description={t('settings.assistant.moderation.extraHint')}
                value={behaviour.extraWords}
                minRows={3}
                disabled={!behaviour.moderationEnabled}
                onChange={(e) => edit({ extraWords: e.currentTarget.value })}
              />
              <Textarea
                label={t('settings.assistant.moderation.allowed')}
                description={t('settings.assistant.moderation.allowedHint')}
                value={behaviour.allowedWords}
                minRows={2}
                disabled={!behaviour.moderationEnabled}
                onChange={(e) => edit({ allowedWords: e.currentTarget.value })}
              />
              <div>
                <Button size="xs" variant="subtle" onClick={() => setShowDefaults((v) => !v)}>
                  {showDefaults ? t('settings.assistant.moderation.hideDefault') : t('settings.assistant.moderation.showDefault', { count: defaultWordCount })}
                </Button>
                {showDefaults && (
                  <Card withBorder className="rr-settings__preview">
                    <Text role="caption" c="var(--rr-color-text-muted)">
                      {defaults.data ? defaults.data.data.words.join(', ') : '…'}
                    </Text>
                  </Card>
                )}
              </div>
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="md">
              <div>
                <Title order={5}>{t('settings.assistant.pauseNotice.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.pauseNotice.intro')}</Text>
              </div>
              <Switch
                label={t('settings.assistant.pauseNotice.enabled')}
                checked={behaviour.noticeEnabled}
                onChange={(checked) => edit({ noticeEnabled: checked })}
              />
              <Textarea
                label={t('settings.assistant.pauseNotice.message')}
                description={t('settings.assistant.pauseNotice.hint')}
                value={behaviour.noticeMessage}
                minRows={2}
                maxLength={500}
                disabled={!behaviour.noticeEnabled}
                onChange={(e) => edit({ noticeMessage: e.currentTarget.value })}
              />
            </Stack>
          </Card>

          {/* What the customer is told when a person takes the chat over and hands it back (4 October 2026). */}
          <Card withBorder data-testid="handover-wording">
            <Stack gap="md">
              <div>
                <Title order={5}>{t('settings.assistant.handover.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.assistant.handover.intro')}</Text>
              </div>
              <Textarea
                label={t('settings.assistant.handover.joined')}
                description={t('settings.assistant.handover.hint')}
                placeholder={behaviour.defaultJoined}
                value={behaviour.joinedMessage}
                minRows={2}
                maxLength={500}
                onChange={(e) => edit({ joinedMessage: e.currentTarget.value })}
              />
              <Textarea
                label={t('settings.assistant.handover.back')}
                description={t('settings.assistant.handover.hint')}
                placeholder={behaviour.defaultBack}
                value={behaviour.backMessage}
                minRows={2}
                maxLength={500}
                onChange={(e) => edit({ backMessage: e.currentTarget.value })}
              />
            </Stack>
          </Card>
        </>
      )}

      {multi && <BotChannelsCard bot={bot} />}

      <Group justify="space-between" align="center">
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('settings.assistant.where')} <Link to="/knowledge">{t('nav.knowledge')}</Link> · <Link to="/flows">{t('nav.flows')}</Link>
        </Text>
        <Button size="sm" disabled={!canSave} loading={save.isPending} onClick={() => save.mutate()}>
          {t('action.save')}
        </Button>
      </Group>

      {multi && <BotActions bot={bot} />}
    </Stack>
  );
}
